import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MessageCircle, X, Send, RefreshCw, Bot, User as UserIcon, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SESSION_KEY = "siv_chatbot_session";
const ANON_ASKED_KEY = "siv_chatbot_anon_used";
const ANON_MESSAGES_KEY = "siv_chatbot_anon_messages";

function getOrCreateSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export default function Chatbot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [anonUsed, setAnonUsed] = useState(false);
  const [sessionId] = useState(() => getOrCreateSessionId());
  const bottomRef = useRef(null);

  // Admin should never see the widget. Il chatbot deve essere sempre visibile
  // per anonimi, client E anche durante il caricamento (evitiamo flash "chatbot mancante")
  const isAdmin = !!user && user.role === "admin";
  const isClient = !!user && user.role === "user";
  const isAnonymous = user === null || user === undefined;
  const visible = !isAdmin;

  // Load history for logged-in clients; load persisted anon messages for anonymous
  useEffect(() => {
    if (!open) return;
    if (isClient) {
      (async () => {
        try {
          const { data } = await api.get(`/chat/history?session_id=${sessionId}`);
          setMessages(data.messages || []);
          setRemaining(data.remaining);
        } catch {}
      })();
    } else if (isAnonymous) {
      const stored = localStorage.getItem(ANON_MESSAGES_KEY);
      if (stored) {
        try {
          setMessages(JSON.parse(stored));
        } catch {}
      }
      setAnonUsed(localStorage.getItem(ANON_ASKED_KEY) === "1");
    }
  }, [open, isClient, isAnonymous, sessionId]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  if (!visible || isAdmin) return null;

  const sendClient = async (text) => {
    setLoading(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setRemaining(data.remaining);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setMessages((m) => m.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const sendAnonymous = async (text) => {
    setLoading(true);
    try {
      const { data } = await api.post("/chat/public", { message: text });
      const next = [
        ...messages,
        { role: "user", content: text },
        { role: "assistant", content: data.reply },
      ];
      setMessages(next);
      localStorage.setItem(ANON_MESSAGES_KEY, JSON.stringify(next));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      // don't keep optimistic user message on error
      setMessages((m) => m.filter((mm, i) => !(i === m.length - 1 && mm.role === "user" && mm.content === text)));
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    if (isClient) {
      setMessages((m) => [...m, { role: "user", content: text }]);
      await sendClient(text);
    } else {
      // optimistic add is inside sendAnonymous to keep localStorage in sync
      setMessages((m) => [...m, { role: "user", content: text }]);
      await sendAnonymous(text);
    }
  };

  const inputDisabled = loading;

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-fab"
        aria-label="Assistente virtuale"
        className="fixed z-40 bottom-24 right-6 w-14 h-14 rounded-full bg-brand text-white shadow-brand-glow hover:bg-brand-dark flex items-center justify-center transition-transform hover:-translate-y-0.5"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="fixed z-40 bottom-44 right-6 w-[92vw] max-w-[400px] h-[560px] bg-white border border-line rounded-lg shadow-2xl flex flex-col overflow-hidden"
            data-testid="chatbot-panel"
          >
            {/* header */}
            <div className="bg-bg-dark text-white px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-brand" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm">Assistente Iperammortamento</div>
                <div className="text-[10px] text-white/50 font-mono">
                  {isClient
                    ? remaining !== null
                      ? `${remaining} messaggi rimasti oggi`
                      : "Studio Ingegneria Valore"
                    : "Studio Ingegneria Valore"}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="chatbot-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-bg" data-testid="chatbot-messages">
              {messages.length === 0 && (
                <div className="text-sm text-ink-muted bg-white rounded-lg p-4 border border-line">
                  {isClient ? (
                    <>
                      Ciao <strong className="text-ink">{user.referente_nome || user.ragione_sociale}</strong>! Sono
                      l'assistente virtuale dello Studio.
                    </>
                  ) : (
                    <>
                      Ciao! Sono l'<strong className="text-ink">assistente virtuale</strong> dello Studio Ingegneria Valore.
                    </>
                  )}
                  <br />
                  Posso aiutarti su Iperammortamento 2026 (L. 199/2025), Nuova Sabatini e agevolazioni correlate.
                  <span className="mt-2 block text-xs text-ink-muted">Esempi:</span>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>· Come si calcola la maggiorazione oltre i 2,5 M€?</li>
                    <li>· Quali beni sono agevolabili in Industria 4.0?</li>
                    <li>· Sabatini Green: aliquote e requisiti?</li>
                  </ul>
                </div>
              )}
              {messages.map((m, i) => (
                <Message key={i} m={m} />
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-ink-muted text-sm" data-testid="chatbot-typing">
                  <RefreshCw className="w-4 h-4 animate-spin text-brand" />
                  <span>Sto pensando...</span>
                </div>
              )}
              {isAnonymous && anonUsed && !loading && messages.length > 3 && (
                <div className="bg-brand/[0.06] border border-brand/30 rounded-lg p-4 text-sm" data-testid="anon-cta">
                  <div className="font-display uppercase text-brand text-[10px] tracking-widest">Serve un approfondimento?</div>
                  <p className="mt-2 text-ink">
                    Contatta il team di Studio Ingegneria Valore per una consulenza personalizzata
                    sul tuo progetto di investimento.
                  </p>
                  <div className="mt-3">
                    <Link
                      to="/contattaci"
                      onClick={() => setOpen(false)}
                      data-testid="anon-contact-cta"
                      className="h-10 inline-flex items-center justify-center gap-2 bg-brand text-white rounded font-display uppercase tracking-widest text-xs px-4 hover:bg-brand-dark"
                    >
                      Contattaci
                    </Link>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* input */}
            <div className="border-t border-line p-3 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Scrivi la tua domanda..."
                  rows={1}
                  disabled={inputDisabled}
                  data-testid="chatbot-input"
                  className="flex-1 resize-none px-3 py-2 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm max-h-[120px] disabled:bg-line/30 disabled:cursor-not-allowed"
                />
                <button
                  onClick={send}
                  disabled={inputDisabled || !input.trim()}
                  data-testid="chatbot-send"
                  className="h-10 w-10 flex-shrink-0 rounded-md bg-brand text-white flex items-center justify-center hover:bg-brand-dark disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="mt-2 text-[10px] text-ink-muted">
                Le risposte si basano sulle fonti ufficiali (L. 199/2025 e decreti attuativi). Per valutazioni definitive contatta lo Studio.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Message({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.role}`}>
      <div
        className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
          isUser ? "bg-brand text-white" : "bg-white border border-line text-brand"
        }`}
      >
        {isUser ? <UserIcon className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
          isUser ? "bg-brand text-white" : "bg-white border border-line text-ink"
        }`}
      >
        {m.content}
      </div>
    </div>
  );
}
